import { MessageSquare } from 'lucide-react';
import { cn } from '@/lib/utils.js';
import { Button } from '@/components/ui/button.js';
import { Badge } from '@/components/ui/badge.js';
import { useSessionStore } from '@/stores/sessionStore.js';
import { useNavigate } from 'react-router-dom';
import type { AskUserRequest } from '@/stores/askUserStore.js';

interface InputRequestItemProps {
  request: AskUserRequest;
  onOpenSession?: (sessionId: string | undefined) => void;
}

export function InputRequestItem({ request, onOpenSession }: InputRequestItemProps) {
  const setCurrentSession = useSessionStore((state) => state.setCurrentSession);
  const navigate = useNavigate();

  const handleOpen = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (request.sessionId) {
      setCurrentSession(request.sessionId);
      navigate(`/chat/${request.sessionId}`);
    }
    onOpenSession?.(request.sessionId);
  };

  const handleClick = () => {
    if (request.sessionId) {
      setCurrentSession(request.sessionId);
      navigate(`/chat/${request.sessionId}`);
    }
  };

  const timeAgo = getTimeAgo(request.createdAt);

  return (
    <div
      onClick={handleClick}
      className={cn(
        'group relative flex flex-col gap-2 rounded-lg p-3 transition-all cursor-pointer',
        'bg-blue-50 dark:bg-blue-950/20 border border-blue-400',
        'hover:bg-muted'
      )}
    >
      {/* Header */}
      <div className="flex items-start gap-2">
        <div className="flex-shrink-0 mt-0.5">
          <MessageSquare className="h-4 w-4 text-blue-600 dark:text-blue-400" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-foreground truncate">
            {request.title || 'User input requested'}
          </p>
          <p className="text-xs text-muted-foreground mt-0.5">{timeAgo}</p>
        </div>
      </div>

      {/* Agent and session info */}
      <div className="flex items-center justify-between gap-2 pl-6">
        <div className="text-xs text-muted-foreground truncate">
          {request.fromAgent ? `From ${request.fromAgent}` : 'Assistant'}
        </div>
        {request.sessionId && (
          <Badge variant="outline" className="text-[10px] px-1 py-0">
            {request.sessionId.slice(0, 6)}
          </Badge>
        )}
      </div>

      {/* Prompt preview */}
      <div className="pl-6">
        <p className="text-xs text-foreground/70 line-clamp-2">{request.prompt}</p>
      </div>

      {/* Action button */}
      <div className="flex gap-2 pl-6">
        <Button
          size="sm"
          onClick={handleOpen}
          className="flex-1 h-7 text-xs bg-blue-600 hover:bg-blue-700 text-white"
        >
          <MessageSquare className="h-3 w-3 mr-1" />
          Open in chat
        </Button>
      </div>
    </div>
  );
}

function getTimeAgo(timestamp: number): string {
  const seconds = Math.floor((Date.now() - timestamp) / 1000);

  if (seconds < 60) {
    return 'just now';
  }

  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) {
    return `${minutes}m ago`;
  }

  const hours = Math.floor(minutes / 60);
  if (hours < 24) {
    return `${hours}h ago`;
  }

  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}
